import axios from "axios";
import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { API_URL, TOKEN } from "../../../variables/constants";
import { toast } from "react-toastify";

const AddMarque = ({
  show,
  handleClose,
  editMode,
  selectedMarque,
  onAddSuccess,
  onUpdateSuccess, // New prop for update success
  existingMarques, // New prop to receive existing data for client-side check
}) => {
  const [loading, setLoading] = useState(false);
  const [nom, setNom] = useState("");
  const [selectedCategorie, setSelectedCategorie] = useState("");
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/categorie`, {
          headers: {
            Authorization: `Bearer ${TOKEN}`,
            "Content-Type": "application/json",
          },
        });
        setCategories(res.data);
      } catch (err) {
        console.error(err); // Use console.error
        toast.error("Erreur lors de la récupération des catégories.");
      }
    };

    fetchCategories();

    // Reset form fields when modal is shown for adding new or for editing
    if (editMode && selectedMarque) {
      setNom(selectedMarque.libelleParametre || "");
      setSelectedCategorie(selectedMarque.categorie?.categorieId || "");
    } else {
      setNom("");
      setSelectedCategorie("");
    }
  }, [show, editMode, selectedMarque]); // Added 'show' to dependency array

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const normalizeNom = (value) => (value || "").trim().toUpperCase(); // Normalize for case-insensitive checks
    const trimmedNom = nom.trim();
    const selectedCat = categories.find(
      (cat) => cat.categorieId === parseInt(selectedCategorie)
    );

    // --- Duplicate Check Logic ---
    const normalizedNom = normalizeNom(trimmedNom);
    const normalizedCategorieId = selectedCat?.categorieId?.toString();

    if (!editMode) {
      // For adding, check against all existing marques
      const isDuplicate = existingMarques.some(
        (item) =>
          normalizeNom(item.libelleParametre) === normalizedNom &&
          item.categorie?.categorieId?.toString() === normalizedCategorieId
      );

      if (isDuplicate) {
        toast.warn("Une marque avec ce nom et cette catégorie existe déjà.");
        setLoading(false);
        return;
      }
    } else {
      // For editing, check against existing marques EXCLUDING the one being edited
      const isDuplicate = existingMarques.some(
        (item) =>
          item.parametreId?.toString() !== selectedMarque?.parametreId?.toString() && // Exclude the current item
          normalizeNom(item.libelleParametre) === normalizedNom &&
          item.categorie?.categorieId?.toString() === normalizedCategorieId
      );

      if (isDuplicate) {
        toast.warn("Une autre marque avec ce nom et cette catégorie existe déjà.");
        setLoading(false);
        return;
      }
    }
    // --- End Duplicate Check Logic ---

    const marqueData = {
      ...selectedMarque, // Keep existing ID for PUT request
      libelleParametre: normalizedNom, // Save normalized to enforce case-insensitive uniqueness
      typeParametre: "MARQUE",
      categorie: selectedCat || null, // Ensure category is null if not found
    };

    try {
      const response = editMode
        ? await axios.put(`${API_URL}/api/parametre`, marqueData, {
            headers: {
              Authorization: `Bearer ${TOKEN}`,
              "Content-Type": "application/json",
            },
          })
        : await axios.post(`${API_URL}/api/parametre`, marqueData, {
            headers: {
              Authorization: `Bearer ${TOKEN}`,
              "Content-Type": "application/json",
            },
          });

      toast.success(editMode ? "Marque modifiée avec succès" : "Marque ajoutée avec succès");

      if (!editMode) {
        onAddSuccess(response.data); // Add to top of list
      } else {
        onUpdateSuccess(response.data); // Call new update success handler
      }
      handleClose(); // Close modal after success
    } catch (err) {
      console.error(err);
      if (err.response && err.response.status === 409) { // Assuming 409 for conflict/duplicate from API
        toast.error("Erreur : Cette marque existe déjà sur le serveur.");
      } else {
        toast.error("Erreur lors de la sauvegarde de la marque.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered>
      <Modal.Body>
        <button
          type="button"
          className="btn-close btn-primary"
          onClick={handleClose}
          aria-label="Close"
        ></button>
        <div className="text-center mb-4">
          <h4 className="address-title mb-2">
            {editMode ? "Modifier la" : "Ajouter une"} marque
          </h4>
          <p className="address-subtitle">
            {editMode ? "Modifier la" : "Ajouter une nouvelle"} marque
          </p>
        </div>
        <form className="row g-3" onSubmit={onSubmit}>
          <div className="col-12">
            <label className="form-label">Nom</label>
            <input
              type="text"
              className="form-control"
              placeholder="Nom de la marque"
              value={nom}
              onChange={(e) => setNom(e.target.value)}
              required
            />
          </div>
          <div className="col-12">
            <label className="form-label">Catégorie</label>
            <select
              className="form-select"
              value={selectedCategorie}
              onChange={(e) => setSelectedCategorie(e.target.value)}
              required
            >
              <option value="">Sélectionner la catégorie</option>
              {categories.map((cat) => (
                <option key={cat.categorieId} value={cat.categorieId}>
                  {cat.nom}
                </option>
              ))}
            </select>
          </div>
          <div className="col-12 text-center">
            <button type="submit" className="btn btn-primary me-3" disabled={loading}>
              {loading ? (
                <>
                  <span className="spinner-grow spinner-grow-sm me-1" role="status" aria-hidden="true" />
                  Enregistrement...
                </>
              ) : (
                "Enregistrer"
              )}
            </button>
            <button type="button" className="btn btn-secondary" onClick={handleClose}>
              Annuler
            </button>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
};

export default AddMarque;
